/**
 * Sharing API service for project sharing, invitations, and shared document access.
 */

import type { Role, ShareRecord, SharedProject } from '@/lib/types';
import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

export interface ShareProjectResponse {
  shareId: string;
  projectId: string;
  recipientEmail: string;
  role: Role;
  status: string;
  isExistingUser: boolean;
}

export interface SharedWithMeResponse {
  shares: SharedProject[];
  pendingInvitations: SharedProject[];
}

export interface SharedDocumentsResponse {
  documents: SharedDocument[];
  role: Role;
  projectName: string;
  ownerEmail: string;
}

export interface SharedDocument {
  id: string;
  title: string;
  content: string;
  templateType: string;
  createdAt: string;
  updatedAt: string;
  projectId: string;
}

class SharingApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'SharingApi');
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}${path}`, { ...options, headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Request failed: ${response.status}`);
    }
    return response.json();
  }

  /** Share a project with another user by email */
  async shareProject(
    projectId: string,
    recipientEmail: string,
    role: Role
  ): Promise<ShareProjectResponse> {
    this.log.debug('Sharing project', { projectId, recipientEmail, role });
    return this.request<ShareProjectResponse>('/sharing/share', {
      method: 'POST',
      body: JSON.stringify({ projectId, recipientEmail, role }),
    });
  }

  /** List all shares for a project (owner view) */
  async listProjectShares(projectId: string): Promise<{ shares: ShareRecord[] }> {
    return this.request<{ shares: ShareRecord[] }>(`/sharing/project/${projectId}`);
  }

  /** List projects shared with the authenticated user */
  async listSharedWithMe(): Promise<SharedWithMeResponse> {
    return this.request<SharedWithMeResponse>('/sharing/shared-with-me');
  }

  /** Accept or decline a share invitation */
  async respondToShare(
    shareId: string,
    projectId: string,
    action: 'accept' | 'decline'
  ): Promise<{ shareId: string; projectId: string; status: string }> {
    return this.request('/sharing/respond', {
      method: 'POST',
      body: JSON.stringify({ shareId, projectId, action }),
    });
  }

  /** Revoke a share (owner action) */
  async revokeShare(projectId: string, recipientEmail: string): Promise<void> {
    await this.request('/sharing/revoke', {
      method: 'POST',
      body: JSON.stringify({ projectId, recipientEmail }),
    });
  }

  /** Update role for a share (owner action) */
  async updateShareRole(
    projectId: string,
    recipientEmail: string,
    role: Role
  ): Promise<ShareRecord> {
    return this.request<ShareRecord>('/sharing/update-role', {
      method: 'POST',
      body: JSON.stringify({ projectId, recipientEmail, role }),
    });
  }

  /** Get documents for a shared project (recipient view) */
  async getSharedDocuments(projectId: string): Promise<SharedDocumentsResponse> {
    return this.request<SharedDocumentsResponse>(`/sharing/project/${projectId}/documents`);
  }
}

export const sharingApi = new SharingApiService();
